<template>
  <div class>
    <ModalBox v-model="legacyModalVisible" size="sm" hide-close header-class="!hidden" wrapper-classes="lg:!w-[640px] p-6">

      <!-- Header -->
      <div class="mb-5">
        <h4 class="text-lg font-semibold text-white mb-1">Create paywall</h4>
        <p class="text-sm text-surface-7 mb-0">Set your pricing to monetize this content. Viewers get access once they
          pay.</p>
      </div>

      <div class="flex flex-col gap-4">
        <!-- Name -->
        <div>
          <label class="block text-tiny font-medium text-surface-8 mb-1">Paywall name</label>
          <FormInput v-model="name" size="md" placeholder="e.g. Championship Final 2026" />
        </div>

        <!-- Source (placed high so its dropdown has room to expand downward) -->
        <div>
          <label class="block text-tiny font-medium text-surface-8 mb-1">Content source</label>
          <stream-source-dropdown @on-source-selected="onSourceStreamSelected" :dropRight="true"
            :selectedStream="selectedSource" :showPlaylist="true" :hide-notification="true" :close-after-select="true" />
          <p class="text-tiny text-surface-6 mb-0 mt-1">Choose the livestream, VOD, or playlist this paywall protects.</p>
        </div>

        <!-- Payment type (segmented control — no dropdown, no layout shift) -->
        <div>
          <label class="block text-tiny font-medium text-surface-8 mb-1">Payment type</label>
          <TabGroup type="segmented" :cols="2">
            <TabItem v-for="pt in paymentTypes" :key="pt.value"
              :is-active="selectedPaymentType === pt.value"
              @click="selectedPaymentType = pt.value">
              {{ pt.text }}
            </TabItem>
          </TabGroup>
        </div>

        <!-- Currency + Amount -->
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col">
            <label class="block text-tiny font-medium text-surface-8 mb-1">Currency</label>
            <country-currency-dropdown :is-disabled="isFree" @on-currency-selected="onCurrencySelected"
              :selectedCurrency="selectedCurrency" />
          </div>
          <div class="flex flex-col">
            <label class="block text-tiny font-medium text-surface-8 mb-1">Amount</label>
            <FormInput :model-value="isFree ? 0 : selectedPrice" @update:model-value="onAmountInput"
              @keydown="blockNegativeKeys" size="md" type="number" placeholder="0" :disabled="isFree" />
          </div>
        </div>

        <!-- Access length (pay-per-view only) -->
        <div v-if="!isFree">
          <label class="block text-tiny font-medium text-surface-8 mb-1">Access length</label>
          <TabGroup type="segmented" :cols="2">
            <TabItem v-for="al in accessLengthOptions" :key="al.value"
              :is-active="accessLength === al.value"
              @click="accessLength = al.value">
              {{ al.text }}
            </TabItem>
          </TabGroup>
          <div v-if="accessLength === 'limited'" class="grid grid-cols-1 gap-4 mt-2 sm:grid-cols-2">
            <FormInput v-model="accessValue" @keydown="blockNegativeKeys" size="md" type="number" min="1" placeholder="48" />
            <DropDownMenu ref="accessUnitDropdown" type="flat" size="md" full-width class="w-full">
              <template #toggle-button>
                {{ accessUnitLabel }}
                <IconBase class="ml-auto shrink-0">
                  <IconChevronDown />
                </IconBase>
              </template>
              <template #menu-items>
                <DropDownMenuItem v-for="unit in accessUnitOptions" :key="unit.value"
                  :custom-classes="accessUnitItemClasses(unit)"
                  @click="setAccessUnit(unit.value)">
                  {{ unit.text }}
                </DropDownMenuItem>
              </template>
            </DropDownMenu>
          </div>
          <!-- Optional event-anchored start (limited window only). -->
          <div v-if="accessLength === 'limited'" class="grid grid-cols-1 gap-4 mt-2 sm:grid-cols-2">
            <div>
              <label class="flex items-center gap-1 text-tiny font-medium text-surface-8 mb-1">
                <span>Access starts on <span class="text-surface-6 font-normal">(optional)</span></span>
                <Tooltip
                  content="Set this to your event date so access covers the event even for early buyers — e.g. a Saturday game with 2-day access stays available through Saturday + 2 days. Leave empty to start access at purchase.">
                  <icon-base class="text-surface-6 hover:text-surface-8 transition-colors" width="14" height="14">
                    <icon-info-small />
                  </icon-base>
                </Tooltip>
              </label>
              <div class="flex items-center relative">
                <IconBase class="absolute z-10 !left-2 text-surface-8">
                  <icon-calendar />
                </IconBase>
                <IconBase class="absolute z-10 !left-7 text-surface-8">
                  <icon-clock />
                </IconBase>
                <VueDatePicker placeholder="Select start date (optional)" v-model="accessStartAt" utc :clearable="true"
                  inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 h-8 text-tiny font-inter" />
              </div>
              <p class="text-tiny text-surface-7 mb-0 mt-1">Times in your timezone — {{ localTimezoneLabel }}.</p>
            </div>
          </div>
          <p class="text-tiny text-surface-6 mb-0 mt-1">
            {{ accessLength === 'lifetime'
              ? 'Viewers keep access for up to a year after paying.'
              : (accessStartAt
                ? `Access runs for ${accessSummary} starting on the date above; buyers before then keep access until it expires.`
                : `Viewers get access for ${accessSummary} after paying, then it expires.`) }}
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
        <p v-if="disabledReason && !processing" class="text-tiny text-gold mb-0 mr-auto">{{ disabledReason }}</p>
        <FormButton type="secondary" size="md" class="justify-center" @click="unselectForm">Cancel</FormButton>
        <FormButton type="main" size="md" class="justify-center" @click="onConfirm()"
          :disabled="createDisabled || paywallLimitReached || processing">
          {{ processing ? 'Creating…' : 'Create paywall' }}
        </FormButton>
      </div>
    
</ModalBox>
  </div>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import _ from 'lodash';
import CountryCurrencyDropdown from "@/components/ui/CountryCurrencyDropdown.vue"
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import PaywallService from "@/services/PaywallService"
import { mapGetters } from 'vuex'
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconCalendar from '@/components/icon/IconCalendar.vue';
import IconClock from '@/components/icon/IconClock.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import { track, EVENTS } from '@/lib/tracking'

// Backend price is Decimal(8,2); values above this overflow / are rejected by Stripe.
const MAX_PRICE = 999999.99

// Rental units -> seconds (month = 30 days).
const ACCESS_UNIT_SECONDS = { hour: 3600, day: 86400, week: 604800, month: 2592000 }

export default {
  name: 'AddPaywallModal',
	mixins: [legacyModalBridgeMixin],
	legacyModalId: 'add-paywall-modal',
  components: {
		ModalBox,
    FormButton,
		FormInput,
    TabGroup,
    TabItem,
    DropDownMenu,
    DropDownMenuItem,
    CountryCurrencyDropdown,
    StreamSourceDropdown,
    IconBase,
    IconCalendar,
    IconClock,
    IconChevronDown,
    IconInfoSmall,
    Tooltip,
    VueDatePicker
	},
  data() {
    return {
      processing: false,
      name: '',
      selectedSource: null,
      selectedPrice: 0,
      selectedCurrency: 'usd',
      selectedPaymentType: 'payperview',
      paymentTypes: [
        {
          text: "Pay per view",
          value: "payperview",
        },
        {
          text: "Free",
          value: "free",
        },
      ],
      accessLength: 'lifetime',
      accessValue: 48,
      accessUnit: 'hour',
      // Optional event-anchored start (UTC ISO string, null = starts at purchase).
      accessStartAt: null,
      accessLengthOptions: [
        { text: 'Lifetime', value: 'lifetime' },
        { text: 'Limited time', value: 'limited' },
      ],
      accessUnitOptions: [
        { text: 'Hours', value: 'hour' },
        { text: 'Days', value: 'day' },
        { text: 'Weeks', value: 'week' },
        { text: 'Months', value: 'month' },
      ],
    }
  },
  props: {

  },
  computed: {
    ...mapGetters({
      maxPaywalls: "User/maxPaywalls",
      paywalls: "Paywalls/paywalls",
    }),

    isFree() {
      return this.selectedPaymentType === 'free'
    },

    // Rental window in seconds; null = lifetime (or free, which is always lifetime).
    accessSeconds() {
      if (this.isFree || this.accessLength !== 'limited') return null
      const v = Math.trunc(Number(this.accessValue))
      if (!Number.isFinite(v) || v <= 0) return null
      return v * (ACCESS_UNIT_SECONDS[this.accessUnit] || ACCESS_UNIT_SECONDS.hour)
    },

    accessSummary() {
      const v = Math.trunc(Number(this.accessValue))
      const n = Number.isFinite(v) && v > 0 ? v : 0
      return `${n} ${this.accessUnit}${n === 1 ? '' : 's'}`
    },

    accessUnitLabel() {
      return this.accessUnitOptions.find((u) => u.value === this.accessUnit)?.text || 'Hours'
    },

    // Only send a start date for a limited (paid) window; otherwise null.
    // VueDatePicker (`utc` mode) already emits a UTC ISO string; normalize to a
    // UTC ISO string for the payload (matches the edit view and backend contract).
    accessStartPayload() {
      if (this.isFree || this.accessLength !== 'limited' || !this.accessStartAt) return null
      const d = new Date(this.accessStartAt)
      return Number.isNaN(d.getTime()) ? null : d.toISOString()
    },

    // The picked date/time is entered in the creator's local timezone; make it explicit.
    localTimezoneLabel() {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        const off = -new Date().getTimezoneOffset()
        const sign = off >= 0 ? '+' : '-'
        const hh = String(Math.floor(Math.abs(off) / 60)).padStart(2, '0')
        const mm = String(Math.abs(off) % 60).padStart(2, '0')
        return `${tz} (GMT${sign}${hh}:${mm})`
      } catch { return 'your local timezone' }
    },

    createDisabled() {
      if (this.name === '') {
        return true
      }

      if (this.selectedSource === null) {
        return true
      }

      if (Number(this.selectedPrice) < 0 || Number(this.selectedPrice) > MAX_PRICE) {
        return true
      }

      return false
    },
    paywallLimitReached() {
      if (this.paywalls.length >= this.maxPaywalls) {
        return true
      }

      return false
    },
    disabledReason() {
      if (this.paywallLimitReached) {
        return this.maxPaywalls
          ? `You've reached your plan's paywall limit (${this.paywalls.length}/${this.maxPaywalls}). Contact support to raise it.`
          : `Your plan doesn't include paywalls yet. Contact support to enable them.`
      }
      if (!this.name) return 'Enter a paywall name to continue.'
      if (this.selectedSource === null) return 'Choose a content source to continue.'
      if (Number(this.selectedPrice) < 0) return 'Enter a valid amount.'
      if (Number(this.selectedPrice) > MAX_PRICE) return `Amount can't exceed ${MAX_PRICE.toLocaleString()}.`
      return ''
    },
  },
  async mounted() {
  },
  methods: {
    onLegacyModalShow() {
      this.onInit();
    },
    onLegacyModalHide() {
      this.onDismiss();
    },
    onSourceStreamSelected(source) {
      this.selectedSource = source
    },
    onCurrencySelected(currency) {
      this.selectedCurrency = currency.code.toLowerCase()
    },
    accessUnitItemClasses(unit) {
      const base = 'h-9 !px-3 flex items-center w-full text-tiny leading-none truncate transition-colors hover:bg-surface-3 hover:text-white'
      const state = unit.value === this.accessUnit ? 'bg-surface-3 text-white' : 'text-surface-text-1'
      return `${base} ${state}`
    },
    setAccessUnit(unit) {
      this.accessUnit = unit
      this.$refs.accessUnitDropdown?.hide?.(true)
    },
    // Keep the raw value so decimals type naturally, but never allow negatives
    // (covers typing, paste, and the number spinner).
    onAmountInput(val) {
      if (val === '' || val === null || val === undefined) {
        this.selectedPrice = ''
        return
      }
      const num = Number(val)
      if (isNaN(num)) { this.selectedPrice = val; return }
      if (num < 0) { this.selectedPrice = 0; return }
      if (num > MAX_PRICE) { this.selectedPrice = MAX_PRICE; return }
      this.selectedPrice = val
    },
    blockNegativeKeys(e) {
      if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault()
    },
    async onInit() {
    },
    async onConfirm() {
      this.processing = true
      try {
        const base = process.env.VUE_APP_PLAYER_APP_BASE_URL
        const rand = Math.random().toString(36).slice(-7)
        const source = this.selectedSource

        // Mirror PaywallManage's source URL logic so create/edit stay consistent.
        let url
        if (source.streams) {
          // playlist
          url = `${base}/playlist/${source._id}?paywall=1&rand=${rand}`
        } else if (source.fileName) {
          // single VOD file: prefer the file's embedUrl, else fall back to /vod/<videoId>
          let vid = source.id || source._id || ''
          if (typeof vid === 'string' && vid.endsWith('.mp4')) vid = vid.replace('.mp4', '')
          url = source.embedUrl
            ? `${source.embedUrl}${source.embedUrl.includes('?') ? '&' : '?'}paywall=1&rand=${rand}`
            : `${base}/vod/${vid}?paywall=1&rand=${rand}`
        } else if (source.type === 'vod' && source.subfolder?._id) {
          // sub-folder inside a VOD folder: the player scopes the playlist to it
          url = `${base}/vod/playlist/${source._id}?paywall=1&subfolder=${source.subfolder._id}&rand=${rand}`
        } else if (source.type === 'vod') {
          // whole VOD folder
          url = `${base}/vod/playlist/${source._id}?paywall=1&rand=${rand}`
        } else {
          // livestream
          url = `${base}/${source.key}?paywall=1&rand=${rand}`
        }

        const payload = {
          url,
          title: this.name,
          recurring: 0,                           // pay per view
          currency: this.selectedCurrency,
          price: this.selectedPrice,
          period: 'year',
          access_seconds: this.accessSeconds,     // null = lifetime
          access_start_at: this.accessStartPayload, // null = starts at purchase
        }

        const pwObj = await PaywallService.activateStreamPaywall(
          source._id || source.id,
          payload
        );

        let sourceType = source.type
        if (source.streams) sourceType = 'playlist'
        else if (source.fileName) sourceType = 'vod'
        else if (!sourceType) sourceType = 'live'
        const price = Number(this.selectedPrice) || 0
        track(EVENTS.PAYWALL_CREATED, {
          paywallId: pwObj?.id || pwObj?._id,
          sourceType,
          price,
          currency: this.selectedCurrency,
          paymentType: this.selectedPaymentType,
          isFree: price === 0,
        })

        this.$emit('paywall-created', pwObj);
        this.dismiss()

      } catch (err) {
        this.$notify({
          group: "error",
          text: err.message || "Create paywall failed",
        });
      }

      this.processing = false
    },
    unselectForm() {
      this.resetForm();
      this.dismiss();
    },
    onDismiss() {
      this.resetForm();
      // lazy clear
      setTimeout(() => {
        this.$parent.processing = false;
      }, 1000);
    },
    dismiss() {
      this.processing = false;
      this.onDismiss();
      this.hideLegacyModal();
    },
    resetForm() {
      this.selectedSource = null
      this.selectedPrice = 0
      this.selectedCurrency = 'usd'
      this.selectedPaymentType = 'payperview'
      this.accessLength = 'lifetime'
      this.accessValue = 48
      this.accessUnit = 'hour'
      this.accessStartAt = null
    },
  },
  watch: {
    selectedPaymentType(val) {
      // "Free" is just a $0 paywall — force the amount and let the field lock.
      if (val === 'free') this.selectedPrice = 0
    },
    accessLength(val) {
      // Event-anchored start only applies to a limited window.
      if (val !== 'limited') this.accessStartAt = null
    },
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
hr {
  border-top-color: var(--c-dark-4);
}

.cursor-pointer {
  cursor: pointer;
}

.edit-pm:hover {
  opacity: .8;
  transition: all .3s ease;
}

@media (max-width: 991px) {
  .sticky-header {
    background-color: var(--c-dark-2);
    position: sticky;
    top: 0;
    z-index: 1;
  }
}

@media (max-width: 767px) {
  .label-v {
    display: flex;
    align-items: flex-end;
  }
}
</style>
